import React, { useState, useMemo, useEffect } from "react";
import { isToday, isSameDay, startOfDay, addDays, isBefore } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { PlanetaryHour } from "../utils/planetaryHours";
import PlanetaryHoursList from "./PlanetaryHoursList";
import { calculateAllPlanetaryHours } from "../utils/planetaryHours";
import TimezoneSelector from "./TimezoneSelector";
import { Muhurta, calculateAllMuhurtas } from "../utils/muhurtasCalculator";
import { calculateChoghadiyaIntervals } from "../utils/choghadiyaCalculator";
import MuhurtasList from "./MuhurtasList";
import ChoghadiyaList from "./ChoghadiyaList";
import { downloadICSFile } from "../utils/calendarUtils";
import CalendarSaveButton from "./CalendarSaveButton";
import "../styles/SunriseResults.css";

interface SunriseResultsProps {
  loading: boolean;
  location: string;
  defaultLocationName: string;
  sunrise: Date | null;
  sunset: Date | null;
  solarNoon: Date | null;
  gulikaInterval: { start: Date; end: Date } | null;
  rahuInterval: { start: Date; end: Date } | null;
  yamagandamInterval: { start: Date; end: Date } | null;
  brahmaMuhurta: Muhurta | null;
  abhijitMuhurta: Muhurta | null;
  currentPlanetaryHour: PlanetaryHour | null;
  countdown: string;
  progress: number;
  error: string | null;
  selectedDate: Date;
  dataSource?: "tripura" | "sunrise-sunset.org" | "noaa";
  selectedTimezone: string;
  onTimezoneChange: (timezone: string) => void;
}

const SunriseResults: React.FC<SunriseResultsProps> = ({
  loading,
  location,
  defaultLocationName,
  sunrise,
  sunset,
  solarNoon,
  gulikaInterval,
  rahuInterval,
  yamagandamInterval,
  brahmaMuhurta,
  abhijitMuhurta,
  currentPlanetaryHour,
  countdown,
  progress,
  error,
  selectedDate,
  dataSource = "tripura",
  selectedTimezone,
  onTimezoneChange,
}) => {
  const formatTimeForDisplay = (date: Date | null) => {
    if (!date) return "Se încarcă...";
    return formatInTimeZone(date, selectedTimezone, "HH:mm");
  };

  // Determine if we're after midnight but before sunrise
  const now = new Date();
  const isAfterMidnightBeforeSunrise =
    sunrise && isSameDay(now, selectedDate) && isBefore(now, sunrise);

  // For the "after midnight but before sunrise" case, we need yesterday's date
  const referenceDate =
    isAfterMidnightBeforeSunrise && isToday(selectedDate)
      ? addDays(startOfDay(selectedDate), -1)
      : selectedDate;

  // Memoize planetary hours calculation to prevent recalculation on every render
  const allPlanetaryHours = useMemo(() => {
    if (sunrise && sunset) {
      return calculateAllPlanetaryHours(sunrise, sunset, referenceDate);
    }
    return [];
  }, [sunrise, sunset, referenceDate]);

  // Memoize muhurtas calculation
  const allMuhurtas = useMemo(() => {
    if (sunrise && sunset) {
      return calculateAllMuhurtas(sunrise, sunset, referenceDate);
    }
    return [];
  }, [sunrise, sunset, referenceDate]);

  // Calculate next day's sunrise (estimate if not available)
  const nextDaySunrise = useMemo(() => {
    if (!sunrise) return null;
    return addDays(sunrise, 1);
  }, [sunrise]);

  // Memoize choghadiya calculation
  const allChoghadiya = useMemo(() => {
    if (sunrise && sunset && nextDaySunrise) {
      return calculateChoghadiyaIntervals(sunrise, sunset, nextDaySunrise);
    }
    return [];
  }, [sunrise, sunset, nextDaySunrise]);

  const [showPlanetaryHours, setShowPlanetaryHours] = useState(false);
  const [showSpecialIntervals, setShowSpecialIntervals] = useState(false);
  const [showVedicIntervals, setShowVedicIntervals] = useState(false);
  const [activeVedicTab, setActiveVedicTab] = useState<
    "muhurta" | "choghadiya"
  >("muhurta");
  const isSelectedDateToday = isToday(selectedDate);

  const togglePlanetaryHours = () => {
    setShowPlanetaryHours(!showPlanetaryHours);
  };

  const toggleSpecialIntervals = () => {
    setShowSpecialIntervals(!showSpecialIntervals);
  };

  const toggleVedicIntervals = () => {
    setShowVedicIntervals(!showVedicIntervals);
  };

  // Use a ref to track if we've ever had data
  const [hadDataBefore, setHadDataBefore] = useState(false);

  // Update the hadDataBefore state when we have data
  useEffect(() => {
    if (sunrise && sunset && !error) {
      setHadDataBefore(true);
    }
  }, [sunrise, sunset, error]);

  // Determine if we should show the loading overlay
  const showLoadingOverlay = loading && (!hadDataBefore || !sunrise || !sunset);

  const downloadAllEventsAsICS = () => {
    const events = [];
    const dateStr = formatInTimeZone(
      selectedDate,
      selectedTimezone,
      "yyyy-MM-dd",
    );

    if (rahuInterval) {
      events.push({
        title: "Rahu Kala - Interval nefavorabil",
        startTime: rahuInterval.start,
        endTime: rahuInterval.end,
        description:
          "Interval nefavorabil din astrologia vedică. Evitați începerea activităților importante în acest interval.",
      });
    }

    if (gulikaInterval) {
      events.push({
        title: "Gulika",
        startTime: gulikaInterval.start,
        endTime: gulikaInterval.end,
        description: "Interval din astrologia vedică.",
      });
    }

    if (yamagandamInterval) {
      events.push({
        title: "Yamaganda - Interval nefavorabil",
        startTime: yamagandamInterval.start,
        endTime: yamagandamInterval.end,
        description:
          "Interval nefavorabil din astrologia vedică. Evitați începerea activităților importante în acest interval.",
      });
    }

    if (brahmaMuhurta) {
      // For Brahma Muhurta, adjust the date to the previous day
      const adjustedStartTime = new Date(
        brahmaMuhurta.startTime.getTime() - 24 * 60 * 60 * 1000,
      );
      const adjustedEndTime = new Date(
        brahmaMuhurta.endTime.getTime() - 24 * 60 * 60 * 1000,
      );

      events.push({
        title: "Brahma Muhurta - Interval favorabil",
        startTime: adjustedStartTime,
        endTime: adjustedEndTime,
        description:
          "Interval favorabil din astrologia vedică. Ideal pentru meditație, yoga și activități spirituale.",
      });
    }

    if (abhijitMuhurta) {
      events.push({
        title: "Abhijit Muhurta - Interval favorabil",
        startTime: abhijitMuhurta.startTime,
        endTime: abhijitMuhurta.endTime,
        description:
          "Interval favorabil din astrologia vedică. Ideal pentru începerea activităților importante.",
      });
    }

    if (events.length > 0) {
      downloadICSFile(events, `intervale-astrologice-${dateStr}.ics`);
    }
  };

  return (
    <div className={`results-container ${showLoadingOverlay ? "loading" : ""}`}>
      {(sunrise && sunset && !error) ||
      (loading && hadDataBefore && sunrise && sunset) ? (
        <>
          <div className="timezone-container">
            <TimezoneSelector
              selectedTimezone={selectedTimezone}
              onTimezoneChange={onTimezoneChange}
            />
          </div>
          <p className="location-result">
            <span className="result-label"> Locație: </span>
            <span className="location-name">
              {location || defaultLocationName}
            </span>
          </p>
          <div className="times-grid">
            {sunrise && (
              <CalendarSaveButton
                event={{
                  title: `Răsărit - ${formatTimeForDisplay(sunrise)}`,
                  description: `Răsăritul soarelui la ${location || defaultLocationName}`,
                  startTime: sunrise,
                  endTime: new Date(sunrise.getTime() + 5 * 60000), // Add 5 minutes for end time
                }}
                showIcon={false}
              >
                <div className="time-item calendar-clickable">
                  <span className="result-label">
                    <svg
                      className="time-svg sunrise-svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 4V2M12 16C8.69 16 6 13.31 6 10H18C18 13.31 15.31 16 12 16ZM22 22H2M6 22H18M22 10H20M4 10H2M5.45 5.46L4.04 4.04M18.96 4.04L17.54 5.46M12 22V16"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Răsărit
                  </span>
                  <span className="time-value">
                    {" "}
                    {formatTimeForDisplay(sunrise)}{" "}
                  </span>
                </div>
              </CalendarSaveButton>
            )}
            {solarNoon && (
              <CalendarSaveButton
                event={{
                  title: `Zenit (MC) - ${formatTimeForDisplay(solarNoon)}`,
                  description: `Zenitul (soarele la mijlocul cerului) la ${location || defaultLocationName}`,
                  startTime: solarNoon,
                  endTime: new Date(solarNoon.getTime() + 5 * 60000), // Add 5 minutes for end time
                }}
                showIcon={false}
              >
                <div className="time-item calendar-clickable">
                  <span className="result-label">
                    <svg
                      className="time-svg noon-svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <circle
                        cx="12"
                        cy="12"
                        r="5"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <path
                        d="M12 4V2M12 22V20M4 12H2M22 12H20M19.78 4.22L17.66 6.34M17.66 17.66L19.78 19.78M4.22 19.78L6.34 17.66M6.34 6.34L4.22 4.22"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                    Zenit (MC)
                  </span>
                  <span className="time-value">
                    {" "}
                    {formatTimeForDisplay(solarNoon)}{" "}
                  </span>
                </div>
              </CalendarSaveButton>
            )}
            {sunset && (
              <CalendarSaveButton
                event={{
                  title: `Apus - ${formatTimeForDisplay(sunset)}`,
                  description: `Apusul soarelui la ${location || defaultLocationName}`,
                  startTime: sunset,
                  endTime: new Date(sunset.getTime() + 5 * 60000), // Add 5 minutes for end time
                }}
                showIcon={false}
              >
                <div className="time-item calendar-clickable">
                  <span className="result-label">
                    <svg
                      className="time-svg sunset-svg"
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 10C8.69 10 6 12.69 6 16H18C18 12.69 15.31 10 12 10ZM22 22H2M6 22H18M22 16H20M4 16H2M5.45 20.54L4.04 21.96M18.96 21.96L17.54 20.54M12 4V10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Apus
                  </span>
                  <span className="time-value">
                    {" "}
                    {formatTimeForDisplay(sunset)}{" "}
                  </span>
                </div>
              </CalendarSaveButton>
            )}
          </div>

          {/* Special intervals button */}
          <div className="special-intervals-divider"></div>
          <div className="toggle-button-container">
            <button
              className="toggle-special-intervals"
              onClick={toggleSpecialIntervals}
            >
              {showSpecialIntervals
                ? "▼ 󠀠 󠀮󠀮Ascunde intervalele din astrologia vedică"
                : "▶ 󠀠 󠀮󠀮Afișează intervale din astrologia vedică"}
            </button>
          </div>

          {/* Special intervals content */}
          {showSpecialIntervals && (
            <div className="special-intervals-container">
              <div className="special-intervals-header">
                (click pe intervale pentru a le adăuga în Google Calendar sau
                <br />
                <span
                  className="add-all-events-link"
                  onClick={downloadAllEventsAsICS}
                  title="Descarcă fișier .ics pentru Apple Calendar, Outlook, etc."
                >
                  aici pentru a le adăuga pe toate odată
                </span>
                )
              </div>
              <div className="times-grid special-intervals">
                {rahuInterval && (
                  <CalendarSaveButton
                    event={{
                      title: `Rahu Kala - Interval nefavorabil`,
                      description: `Interval nefavorabil din astrologia vedică. Evitați începerea activităților importante în acest interval.`,
                      startTime: rahuInterval.start,
                      endTime: rahuInterval.end,
                    }}
                    showIcon={false}
                  >
                    <div className="time-item special-interval-item calendar-clickable">
                      <span className="result-label">
                        <span className="time-emoji rahu-emoji">⛔️</span>
                        <span className="interval-label-text">Rahu</span>
                      </span>
                      <span className="time-value special-time-value">
                        {formatTimeForDisplay(rahuInterval.start)}-
                        {formatTimeForDisplay(rahuInterval.end)}
                      </span>
                    </div>
                  </CalendarSaveButton>
                )}

                {gulikaInterval && (
                  <CalendarSaveButton
                    event={{
                      title: "Gulika",
                      description: "Interval din astrologia vedică.",
                      startTime: gulikaInterval.start,
                      endTime: gulikaInterval.end,
                    }}
                    showIcon={false}
                  >
                    <div className="time-item special-interval-item calendar-clickable">
                      <span className="result-label">
                        <span className="time-emoji yamagandam-emoji">🪐️</span>
                        <span className="interval-label-text">Gulika</span>
                      </span>
                      <span className="time-value special-time-value">
                        {formatTimeForDisplay(gulikaInterval.start)}-
                        {formatTimeForDisplay(gulikaInterval.end)}
                      </span>
                    </div>
                  </CalendarSaveButton>
                )}

                {yamagandamInterval && (
                  <CalendarSaveButton
                    event={{
                      title: "Yamaganda - Interval nefavorabil",
                      description:
                        "Interval nefavorabil din astrologia vedică. Evitați începerea activităților importante în acest interval.",
                      startTime: yamagandamInterval.start,
                      endTime: yamagandamInterval.end,
                    }}
                    showIcon={false}
                  >
                    <div className="time-item special-interval-item calendar-clickable">
                      <span className="result-label">
                        <span className="time-emoji yamagandam-emoji">⛔️</span>
                        <span className="interval-label-text">Yamaganda</span>
                      </span>
                      <span className="time-value special-time-value">
                        {formatTimeForDisplay(yamagandamInterval.start)}-
                        {formatTimeForDisplay(yamagandamInterval.end)}
                      </span>
                    </div>
                  </CalendarSaveButton>
                )}

                {brahmaMuhurta && (
                  <CalendarSaveButton
                    event={{
                      title: "Brahma Muhurta - Interval favorabil",
                      description:
                        "Interval favorabil din astrologia vedică. Ideal pentru meditație, yoga și activități spirituale.",
                      // For Brahma Muhurta, we need to adjust the date to the previous day
                      // since it occurs before sunrise
                      startTime: new Date(
                        brahmaMuhurta.startTime.getTime() - 24 * 60 * 60 * 1000,
                      ),
                      endTime: new Date(
                        brahmaMuhurta.endTime.getTime() - 24 * 60 * 60 * 1000,
                      ),
                    }}
                    showIcon={false}
                  >
                    <div className="time-item special-interval-item calendar-clickable">
                      <span className="result-label muhurta-label">
                        <span className="time-emoji brahma-emoji">⚫️</span>
                        <span className="muhurta-label-text">
                          Brahma Muhurta
                        </span>
                      </span>
                      <span className="time-value special-time-value">
                        {formatTimeForDisplay(brahmaMuhurta.startTime)}-
                        {formatTimeForDisplay(brahmaMuhurta.endTime)}
                      </span>
                    </div>
                  </CalendarSaveButton>
                )}

                {abhijitMuhurta && (
                  <CalendarSaveButton
                    event={{
                      title: "Abhijit Muhurta - Interval favorabil",
                      description:
                        "Interval favorabil din astrologia vedică. Ideal pentru începerea activităților importante.",
                      startTime: abhijitMuhurta.startTime,
                      endTime: abhijitMuhurta.endTime,
                    }}
                    showIcon={false}
                  >
                    <div className="time-item special-interval-item calendar-clickable">
                      <span className="result-label muhurta-label">
                        <span className="time-emoji abhijit-emoji">🔆</span>
                        <span className="muhurta-label-text">
                          Abhijit Muhurta
                        </span>
                      </span>
                      <span className="time-value special-time-value">
                        {formatTimeForDisplay(abhijitMuhurta.startTime)}-
                        {formatTimeForDisplay(abhijitMuhurta.endTime)}
                      </span>
                    </div>
                  </CalendarSaveButton>
                )}
              </div>
              <div className="more-intervals-container">
                <div
                  className="more-intervals-link"
                  onClick={toggleVedicIntervals}
                >
                  <span className="add-all-events-link">
                    Mai multe intervale astrologice indiene
                  </span>
                </div>
              </div>

              {/* Vedic Intervals Drawer */}
              {showVedicIntervals && (
                <div className="vedic-intervals-drawer">
                  <div className="vedic-intervals-tabs">
                    <button
                      className={`vedic-tab ${activeVedicTab === "muhurta" ? "active" : ""}`}
                      onClick={() => setActiveVedicTab("muhurta")}
                    >
                      Muhurta
                    </button>
                    <button
                      className={`vedic-tab ${activeVedicTab === "choghadiya" ? "active" : ""}`}
                      onClick={() => setActiveVedicTab("choghadiya")}
                    >
                      Choghadiya
                    </button>
                  </div>

                  <div className="vedic-intervals-content">
                    {activeVedicTab === "muhurta" && allMuhurtas.length > 0 && (
                      <MuhurtasList
                        muhurtas={allMuhurtas}
                        selectedTimezone={selectedTimezone}
                      />
                    )}

                    {activeVedicTab === "choghadiya" &&
                      allChoghadiya.length > 0 && (
                        <ChoghadiyaList
                          choghadiyaIntervals={allChoghadiya}
                          selectedTimezone={selectedTimezone}
                        />
                      )}
                  </div>
                </div>
              )}
            </div>
          )}

          {isSelectedDateToday && currentPlanetaryHour && (
            <div className="planetary-hour">
              <h3>Ora planetară curentă: </h3>
              <div className="planet-display">
                <span className="planet-symbol">
                  {" "}
                  {currentPlanetaryHour.symbol}{" "}
                </span>
                <span className="planet-name">
                  {" "}
                  {currentPlanetaryHour.planet}{" "}
                </span>
              </div>
              <div className="hour-progress-container">
                <div className="hour-progress-labels">
                  <span className="start-time">
                    {" "}
                    {formatInTimeZone(
                      currentPlanetaryHour.startTime,
                      selectedTimezone,
                      "HH:mm",
                    )}{" "}
                  </span>
                  <span className="end-time">
                    {" "}
                    {formatInTimeZone(
                      currentPlanetaryHour.endTime,
                      selectedTimezone,
                      "HH:mm",
                    )}{" "}
                  </span>
                </div>
                <div className="hour-progress-bar">
                  <div
                    className="hour-progress-fill"
                    style={{ width: `${progress}%` }}
                  >
                    {" "}
                  </div>
                </div>
                <div className="hour-progress-text"> {progress} % </div>
              </div>

              <p className="next-update">
                Următoarea oră {countdown}: →
                <span className="planet-symbol">
                  {" "}
                  {currentPlanetaryHour.nextSymbol}{" "}
                </span>
                <span className="next-planet">
                  {" "}
                  {currentPlanetaryHour.nextPlanet}{" "}
                </span>
              </p>
            </div>
          )}

          {allPlanetaryHours.length > 0 && (
            <div className="toggle-button-container">
              <button
                className="toggle-planetary-hours"
                onClick={togglePlanetaryHours}
              >
                {showPlanetaryHours
                  ? "▼󠀠󠀠󠀠 󠀠 󠀮󠀮Ascunde Orele Planetare"
                  : "▶ 󠀠 󠀮󠀮Afișează toate Orele Planetare ale zilei"}
              </button>
            </div>
          )}

          {showPlanetaryHours && allPlanetaryHours.length > 0 && (
            <PlanetaryHoursList
              hours={allPlanetaryHours}
              selectedTimezone={selectedTimezone}
            />
          )}

          <div className="attribution">
            {dataSource === "tripura" ? (
              <a
                href="https://nitya.aplicatii.yoga/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {" "}
                Date furnizate de Tripura Astronomy API{" "}
              </a>
            ) : dataSource === "noaa" ? (
              <a
                href="https://gml.noaa.gov/grad/solcalc/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {" "}
                Date furnizate de NOAA Solar Calculator{" "}
              </a>
            ) : (
              <a
                href="https://sunrise-sunset.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                {" "}
                Date furnizate de sunrise - sunset.org{" "}
              </a>
            )}
            <br />
            <a
              href="https://nominatim.org/"
              target="_blank"
              rel="noopener noreferrer"
            >
              {" "}
              Geocodare furnizată de Nominatim, © OpenStreetMap
              contributors{" "}
            </a>
          </div>
        </>
      ) : (
        <div className="loading-placeholder">
          <p>Se încarcă datele...</p>
        </div>
      )}
    </div>
  );
};

export default SunriseResults;
